import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, IconButton } from "@mui/material";
import { Edit, Delete as DeleteIcon } from "components/icons";
import { useSelector } from "react-redux";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  loadManagersPageFromBackend,
  loadAllManagersFromBackend,
  deleteManager,
} from "app/services/manager.service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { managerPageActions } from "store/manager-page-slice";
import { useServerPaginatedGrid } from "app/hooks/useServerPaginatedGrid";
import { PaginatedDataGrid } from "app/components/common/PaginatedDataGrid";
import AddManagerDialog from "./AddManagerDialog";
import { ConfirmDialog } from "../ConfirmDialog";

// Frontend grid field -> backend sortBy column (allowlisted server-side).
const SORT_FIELD_MAP = {
  id: "id",
  name: "name",
  clientEmail: "clientEmail",
};

const AllManagersView = () => {
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  // The "Client / Account" column shows the client name resolved from clientId. Clients are
  // loaded into state.client.rows by MasterRouterFactory; this is a display-only join, so the
  // column is not server-sortable.
  const clientsData = useSelector((state) => state.client.rows);
  const clientNameById = useMemo(() => {
    const map = {};
    (clientsData || []).forEach((c) => {
      map[c.id] = c.name;
    });
    return map;
  }, [clientsData]);

  // The "Reports To" column shows the parent manager's name resolved from parentManagerId.
  // The paginated page returns only parentManagerId (the parent may sit on another page),
  // so load the full manager list once and resolve names client-side — same display-only
  // join the Client / Account column does. Not server-sortable for the same reason.
  const [managerNameById, setManagerNameById] = useState({});
  const loadManagerNames = useCallback(() => {
    loadAllManagersFromBackend()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const map = {};
        list.forEach((m) => {
          map[m.id] = m.name;
        });
        setManagerNameById(map);
      })
      // Keep whatever names we already resolved. Blanking the map would empty every Reports To
      // cell with no error shown, which reads as "the save wiped my hierarchy" — and this now
      // runs after every save, not just on mount.
      .catch((error) => handleAxiosError(error));
  }, []);
  useEffect(() => {
    loadManagerNames();
  }, [loadManagerNames]);

  const {
    rows,
    totalElements,
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchQuery,
    loading,
    fetchPage,
    handlePaginationModelChange,
    handleSortModelChange,
    handleSearchChange,
  } = useServerPaginatedGrid({
    selector: (state) => state.managerPage,
    actions: managerPageActions,
    fetchFn: loadManagersPageFromBackend,
  });

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(`Are you sure you want to delete this Client Manager (Name: '${data.name}')?`);
    setDeleteId(data.id);
    setConfirmOpen(true);
  };

  // The Reports To column is resolved from managerNameById, so anything that changes the manager
  // list has to refresh that map too — otherwise the column keeps showing the previous name.
  const refreshManagers = useCallback(() => {
    fetchPage();
    loadManagerNames();
  }, [fetchPage, loadManagerNames]);

  const deleteHandler = async () => {
    await deleteManager(deleteId)
      .then(() => refreshManagers())
      .catch((error) => handleAxiosError(error));
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton aria-label="Edit" onClick={() => editEventHandler(obj.row)} color="inherit">
          <Edit />
        </IconButton>,
        <IconButton aria-label="Delete" onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
          <DeleteIcon />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "Id", width: 120 },
    { field: "name", headerName: "Name", width: 250 },
    {
      field: "clientId",
      headerName: "Client / Account",
      width: 250,
      sortable: false,
      valueGetter: (value, row) => clientNameById[row.clientId],
    },
    { field: "clientEmail", headerName: "Email", width: 250 },
    {
      field: "parentManagerId",
      headerName: "Reports To",
      width: 200,
      sortable: false,
      valueGetter: (value, row) =>
        row.parentManagerId ? managerNameById[row.parentManagerId] || "" : "",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            InOpTra's Managers - Client Mappings
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <PaginatedDataGrid
            rows={rows}
            columns={columns}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDirection={sortDirection}
            searchQuery={searchQuery}
            isLoading={loading}
            sortFieldMap={SORT_FIELD_MAP}
            defaultSortBy="name"
            defaultSortDirection="ASC"
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            onSearchChange={handleSearchChange}
            onAdd={() => {
              setEditObject(null);
              setOpen(true);
            }}
            addLabel="Add InOpTra's Manager"
            columnVisibilityModel={{ id: false }}
          />
        </MDBox>
      </Card>
      <AddManagerDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={refreshManagers}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This Manager?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </div>
  );
};

export default AllManagersView;
